/**
 * Landing-page engagement tracking for Viluu LandingV2.
 */
import { trackEvent } from './ga4';

const SECTION_SELECTOR = '[data-ga-section]';
const seenSections = new Set();
let clickBound = false;
let faqBound = false;

function sectionName(el) {
  return el.getAttribute('data-ga-section') || el.id || '';
}

function initSectionViews() {
  if (typeof IntersectionObserver === 'undefined') {
    return;
  }
  const sections = document.querySelectorAll(SECTION_SELECTOR);
  if (!sections.length) {
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
          return;
        }
        const name = sectionName(entry.target);
        if (!name || seenSections.has(name)) {
          return;
        }
        seenSections.add(name);
        trackEvent('section_view', { section_name: name });
        observer.unobserve(entry.target);
      });
    },
    { threshold: [0.5] }
  );
  sections.forEach((section) => observer.observe(section));
}

function linkText(el) {
  return (el.textContent || '').replace(/\s+/g, ' ').trim();
}

function resolveLinkLocation(el) {
  if (el.closest('[data-ga-nav="header"], header, .the-nav, .nav')) {
    return 'header';
  }
  if (el.closest('[data-ga-nav="footer"], footer, .the-footer')) {
    return 'footer';
  }
  return 'content';
}

function resolveNetwork(el) {
  const explicit = el.getAttribute('data-network');
  if (explicit) {
    return explicit;
  }
  const href = (el.getAttribute('href') || '').toLowerCase();
  if (href.includes('instagram.com')) {
    return 'instagram';
  }
  if (href.includes('facebook.com')) {
    return 'facebook';
  }
  if (href.includes('tiktok.com')) {
    return 'tiktok';
  }
  return null;
}

function truncate(text, max) {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) {
    return s;
  }
  return s.slice(0, max);
}

function onDocumentClick(event) {
  const anchor = event.target.closest('a[href]');
  if (!anchor) {
    return;
  }

  if (anchor.hasAttribute('data-ga-nav-link')) {
    const text = linkText(anchor);
    if (text) {
      trackEvent('nav_click', {
        link_text: text,
        link_location: resolveLinkLocation(anchor),
      });
    }
  }

  if (anchor.hasAttribute('data-ga-content-link')) {
    trackEvent('content_link_click', {
      link_text: linkText(anchor),
      section_name: anchor.getAttribute('data-ga-section') || '',
    });
  }

  if (anchor.hasAttribute('data-city')) {
    trackEvent('city_click', {
      city: anchor.getAttribute('data-city') || '',
      country: anchor.getAttribute('data-country') || '',
    });
  }

  const network = resolveNetwork(anchor);
  if (network && (anchor.hasAttribute('data-network') || anchor.closest('[data-ga-nav="footer"]'))) {
    trackEvent('social_click', { network });
  }

  const href = (anchor.getAttribute('href') || '').toLowerCase();
  if (href.startsWith('mailto:') && anchor.hasAttribute('data-ga-support')) {
    trackEvent('support_click', { link_text: linkText(anchor) });
  }

  if (anchor.hasAttribute('data-ga-app-store')) {
    trackEvent('app_store_click', {
      store: 'google',
      cta_location: anchor.getAttribute('data-cta-location') || 'unknown',
    });
  }
}

function onFaqToggle(event) {
  const details = event.target.closest('details[data-ga-faq]');
  if (!details || !details.open) {
    return;
  }
  const summary = details.querySelector('summary');
  const question = truncate(linkText(summary), 100);
  if (question) {
    trackEvent('faq_open', { faq_question: question });
  }
}

export function initLandingAnalytics() {
  if (typeof document === 'undefined') {
    return;
  }
  initSectionViews();
  if (!clickBound) {
    clickBound = true;
    document.addEventListener('click', onDocumentClick, true);
  }
  if (!faqBound) {
    faqBound = true;
    document.addEventListener('toggle', onFaqToggle, true);
  }
}
